import { TRUSTPILOT } from "@/config/trustpilot";

import { ExternalLink } from "lucide-react";

const TRUSTPILOT_GREEN = "#00B67A";

function TrustpilotStar({ fill = 1, size = 22 }: { fill?: number; size?: number }) {
  const id = `tp-star-${Math.round(fill * 100)}-${size}`;
  return (
    <span
      className="inline-flex items-center justify-center rounded-[3px]"
      style={{ background: fill > 0 ? TRUSTPILOT_GREEN : "#DCDCE6", width: size, height: size }}
      aria-hidden="true"
    >
      {fill > 0 && fill < 1 ? (
        <svg width={size} height={size} viewBox="0 0 24 24" style={{ position: "absolute" }}>
          <defs>
            <linearGradient id={id}>
              <stop offset={`${fill * 100}%`} stopColor={TRUSTPILOT_GREEN} />
              <stop offset={`${fill * 100}%`} stopColor="#DCDCE6" />
            </linearGradient>
          </defs>
          <rect width="24" height="24" fill={`url(#${id})`} rx="3" />
        </svg>
      ) : null}
      <svg
        width={size * 0.72}
        height={size * 0.72}
        viewBox="0 0 24 24"
        fill="#fff"
        style={{ position: "relative" }}
      >
        <path d="M12 2.5l2.9 6.1 6.6.9-4.8 4.6 1.2 6.6-5.9-3.2-5.9 3.2 1.2-6.6L2.5 9.5l6.6-.9L12 2.5z" />
      </svg>
    </span>
  );
}

function Stars({ size = 22 }: { size?: number }) {
  const { rating } = TRUSTPILOT;
  return (
    <span className="flex items-center gap-1" role="img" aria-label={`${rating} out of ${TRUSTPILOT.maxRating} stars`}>
      {Array.from({ length: TRUSTPILOT.maxRating }).map((_, i) => (
        <TrustpilotStar key={i} size={size} fill={Math.max(0, Math.min(1, rating - i))} />
      ))}
    </span>
  );
}

function TrustpilotWordmark({ className = "" }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 2.5l2.9 6.1 6.6.9-4.8 4.6 1.2 6.6-5.9-3.2-5.9 3.2 1.2-6.6L2.5 9.5l6.6-.9L12 2.5z"
          fill={TRUSTPILOT_GREEN}
        />
      </svg>
      <span className="text-sm font-semibold tracking-tight text-foreground">Trustpilot</span>
    </span>
  );
}

export function TrustpilotStrip() {
  const { rating, maxRating, reviewCount, ratingLabel, profileUrl } = TRUSTPILOT;

  return (
    <section aria-labelledby="trustpilot-heading" className="w-full">
<div className="mx-auto flex w-fit max-w-full flex-wrap items-center justify-center gap-x-2.5 gap-y-1 rounded-full border border-border/60 bg-card/85 px-3 py-1.5 shadow-sm backdrop-blur">
        <TrustpilotWordmark />
        <Stars size={13} />
        <h2 id="trustpilot-heading" className="text-[11px] font-medium text-foreground">
          {rating}/{maxRating} {ratingLabel}
          <span className="text-muted-foreground"> · {reviewCount} reviews</span>
        </h2>
        <a
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary underline-offset-4 hover:underline"
        >
          Read reviews
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      </div>
    </section>
  );
}

